import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCog, Plus, Trash2, Edit2, Eye, EyeOff, Calendar, Save, X, Settings, ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const AccountAnnouncementManager = ({ token }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: "", content: "", type: "info", is_active: true, start_date: "", end_date: ""
  });
  const [settings, setSettings] = useState({ max_display: 3, auto_scroll: true, scroll_interval: 5 });

  useEffect(() => { fetchAnnouncements(); fetchSettings(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/admin/account-announcements`, { headers: { Authorization: `Bearer ${token}` } });
      setAnnouncements(res.data);
    } catch (error) { toast.error("Failed to load account announcements"); }
    finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/account-announcements/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data);
    } catch (error) { /* defaults */ }
  };

  const saveSettings = async () => {
    try {
      await axios.put(
        `${API}/admin/account-announcements/settings?max_display=${settings.max_display}&auto_scroll=${settings.auto_scroll}&scroll_interval=${settings.scroll_interval}`,
        null, { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Settings saved");
      setShowSettings(false);
    } catch (error) { toast.error("Failed to save settings"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/account-announcements/${editingId}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Announcement updated");
      } else {
        await axios.post(`${API}/admin/account-announcements`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Announcement created");
      }
      resetForm();
      fetchAnnouncements();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save announcement"); }
  };

  const handleEdit = (ann) => {
    setFormData({
      title: ann.title, content: ann.content, type: ann.type, is_active: ann.is_active,
      start_date: ann.start_date ? ann.start_date.split("T")[0] : "",
      end_date: ann.end_date ? ann.end_date.split("T")[0] : ""
    });
    setEditingId(ann.id);
    setShowForm(true);
  };

  const handleToggleActive = async (ann) => {
    try {
      await axios.put(`${API}/admin/account-announcements/${ann.id}`, { is_active: !ann.is_active }, { headers: { Authorization: `Bearer ${token}` } });
      fetchAnnouncements();
      toast.success(`Announcement ${ann.is_active ? "hidden" : "shown"}`);
    } catch (error) { toast.error("Failed to update announcement"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`${API}/admin/account-announcements/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch (error) { toast.error("Failed to delete announcement"); }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newOrder = [...announcements];
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    setAnnouncements(newOrder);
    try {
      await axios.put(`${API}/admin/account-announcements/reorder`, { order: newOrder.map(a => a.id) }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) { toast.error("Failed to save order"); fetchAnnouncements(); }
  };

  const handleMoveDown = async (index) => {
    if (index === announcements.length - 1) return;
    const newOrder = [...announcements];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setAnnouncements(newOrder);
    try {
      await axios.put(`${API}/admin/account-announcements/reorder`, { order: newOrder.map(a => a.id) }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) { toast.error("Failed to save order"); fetchAnnouncements(); }
  };

  const resetForm = () => {
    setFormData({ title: "", content: "", type: "info", is_active: true, start_date: "", end_date: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const typeColors = {
    info: "bg-blue-100 text-blue-700",
    event: "bg-purple-100 text-purple-700",
    promo: "bg-green-100 text-green-700",
    alert: "bg-amber-100 text-amber-700"
  };

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <UserCog className="w-5 h-5" />
          Account Page Announcements
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowSettings(!showSettings)} size="sm" variant="outline">
            <Settings className="w-4 h-4 mr-1" />Settings
          </Button>
          <Button onClick={() => setShowForm(!showForm)} size="sm">
            {showForm ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            {showForm ? "Cancel" : "New"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showSettings && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold mb-4 flex items-center gap-2"><Settings className="w-4 h-4" />Display Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Max Announcements to Display</Label>
                <Input type="number" min="1" max="10" value={settings.max_display} onChange={(e) => setSettings({ ...settings, max_display: parseInt(e.target.value) || 1 })} />
              </div>
              <div>
                <Label>Auto-scroll Interval (seconds)</Label>
                <Input type="number" min="3" max="30" value={settings.scroll_interval} onChange={(e) => setSettings({ ...settings, scroll_interval: parseInt(e.target.value) || 5 })} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" id="acct_auto_scroll" checked={settings.auto_scroll} onChange={(e) => setSettings({ ...settings, auto_scroll: e.target.checked })} className="w-4 h-4" />
                <Label htmlFor="acct_auto_scroll">Enable auto-scroll</Label>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={saveSettings} size="sm" className="bg-blue-500 hover:bg-blue-600"><Save className="w-4 h-4 mr-1" />Save</Button>
              <Button onClick={() => setShowSettings(false)} size="sm" variant="outline">Cancel</Button>
            </div>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Announcement title" required />
              </div>
              <div>
                <Label>Type</Label>
                <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full px-3 py-2 border rounded-md">
                  <option value="info">Info</option>
                  <option value="event">Event</option>
                  <option value="promo">Promo</option>
                  <option value="alert">Alert</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Content (HTML supported)</Label>
              <textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} placeholder='Announcement content...' className="w-full px-3 py-2 border rounded-md min-h-[100px] font-mono text-sm" required />
              <p className="text-xs text-gray-500 mt-1">HTML tags supported: &lt;a href="..."&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;br&gt;</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Start Date (optional)</Label><Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} /></div>
              <div><Label>End Date (optional)</Label><Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="acct_is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="w-4 h-4" />
              <Label htmlFor="acct_is_active">Active (visible to users)</Label>
            </div>
            <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600"><Save className="w-4 h-4 mr-2" />{editingId ? "Update" : "Create"} Announcement</Button>
          </form>
        )}

        {loading ? (
          <p className="text-center py-8 text-gray-500">Loading...</p>
        ) : announcements.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No account announcements yet. These appear on the Account page.</p>
        ) : (
          <div className="space-y-3">
            {announcements.map((ann, index) => (
              <div key={ann.id} className={`p-4 border rounded-lg ${ann.is_active ? "bg-white" : "bg-gray-100 opacity-70"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => handleMoveUp(index)} disabled={index === 0} className="h-6 w-6 p-0"><ChevronUp className="w-4 h-4" /></Button>
                    <GripVertical className="w-4 h-4 text-gray-400 mx-auto" />
                    <Button size="sm" variant="ghost" onClick={() => handleMoveDown(index)} disabled={index === announcements.length - 1} className="h-6 w-6 p-0"><ChevronDown className="w-4 h-4" /></Button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-400 font-mono">#{index + 1}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors[ann.type]}`}>{ann.type.toUpperCase()}</span>
                      {!ann.is_active && <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-600">Hidden</span>}
                    </div>
                    <h4 className="font-semibold">{ann.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{ann.content}</p>
                    {(ann.start_date || ann.end_date) && (
                      <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {ann.start_date && `From: ${new Date(ann.start_date).toLocaleDateString()}`}
                        {ann.start_date && ann.end_date && " - "}
                        {ann.end_date && `Until: ${new Date(ann.end_date).toLocaleDateString()}`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleToggleActive(ann)} title={ann.is_active ? "Hide" : "Show"}>
                      {ann.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(ann)}><Edit2 className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(ann.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccountAnnouncementManager;
